/**
 * 📧 Create Job Component
 * Job-Erstellungsformular basierend auf mail_gateway.php
 */

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Send, Mail, FileText, Server, Paperclip, Upload, X } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import HtmlPreview from '../Preview/HtmlPreview';
import { useAuth } from '../../services/AuthService';
import AdminLinkPanel from './AdminLinkPanel';

const CreateJob = ({ onJobCreated }) => {
  const [formData, setFormData] = useState({
    recipients: '',
    subject: '',
    message: '',
    from_name: '',
    dynamic_from_email: '',
    use_dynamic_from_email: false,
    reply_to_email: '',
    emails_per_hour: 100,
    is_html: true,
    templates: [],
    attachments: [],
    selected_smtp_servers: [],
    
    // Versandmethode: smtp oder webmail
    send_method: 'smtp',
    selected_webmail_accounts: [],
    webmail_parallel_threads: 2,  // Max 2 parallele Sessions pro Account
    webmail_text_type: 'html', // 'html' oder 'plain'

    // Burst-Send Modus (SMTP)
    burst_mode: false,
    burst_pause_seconds: 0,
    
    // SMTP Pooled Streams (für parallelen Versand)
    use_pooled_streams: false,
    pooled_streams_count: 2,
    max_parallel_smtps: 10, // Wie viele SMTPs gleichzeitig senden
    sequential_streams: 10, // Parallele Streams im sequenziellen Versand
    
    // Nachrichten-Varianten (Round-Robin)
    message_variants: [],
    strip_html_wrapper: true,

    // Text-Encoding (SMTP)
    text_encoding: 'quoted-printable',
    
    // AI-Content-Randomisierung
    ai_randomization_enabled: false,
    ai_randomization_frequency: 'every_100',
    
    // Zero-Width Character Injection (Anti-Spam)
    zerowidth_subject: false,
    zerowidth_fromname: false,
    zerowidth_content: true,  // Content-Injection standardmäßig AN
    
    // PDF/File Randomization
    randomize_attachments: true,  // Attachment-Randomisierung standardmäßig AN
    
    // List-Unsubscribe Header
    list_unsubscribe_enabled: false,
    list_unsubscribe_url: '{Domain}/unsubscribe',
    list_unsubscribe_method: 'oneclick', // oneclick, mailto, both
    
    // Precedence Header
    precedence_header: false
  });

  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedLinksData, setGeneratedLinksData] = useState(null); // Für Admin Link Panel
  
  // 🤖 AI-Generation Progress
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiProgress, setAiProgress] = useState({ current: 0, total: 0 });
  const [showTemplates, setShowTemplates] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // 🌍 Proxy-Auswahl entfernt:
  // Proxy wird global pro SMTP aus der DB genutzt (711 Session in smtp_servers.custom_proxy_*).
  // Frontend sendet keine proxy_settings mehr, Backend ignoriert proxy_settings ebenfalls.

  const splitMessageVariantsFromContent = (content) => {
    const raw = String(content || '');
    if (!raw.trim()) return [];
    const delimiter = /<!--\s*(?:letter|template)\s*-->/i;
    if (delimiter.test(raw)) {
      return raw
        .split(delimiter)
        .map(part => String(part || '').trim())
        .filter(part => part.length > 0);
    }
    return [raw.trim()];
  };

  const normalizeVariants = (variants) => {
    const cleaned = (variants || [])
      .map(value => String(value || '').trim())
      .filter(value => value.length > 0);
    return Array.from(new Set(cleaned));
  };

  const setSendMethod = (method) => {
    setFormData(prev => ({
      ...prev,
      send_method: method,
      burst_mode: method === 'burst'
    }));
  };

  const stripHtmlWrapper = (content) => {
    if (!formData.strip_html_wrapper) return content;
    if (!content) return content;
    let cleaned = String(content);
    cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '');
    cleaned = cleaned.replace(/<body[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/body>/gi, '');
    return cleaned.trim();
  };

  const handleAddMessageVariant = () => {
    setFormData(prev => ({
      ...prev,
      message_variants: [...(prev.message_variants || []), '']
    }));
  };

  const handleRemoveMessageVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      message_variants: (prev.message_variants || []).filter((_, i) => i !== index)
    }));
  };

  const handleMessageVariantChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      message_variants: (prev.message_variants || []).map((item, i) => (i === index ? value : item))
    }));
  };

  const handleMessageVariantsUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    try {
      const contents = await Promise.all(files.map(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      })));
      
      const importedVariants = normalizeVariants(
        contents.flatMap(splitMessageVariantsFromContent).map(stripHtmlWrapper)
      );
      
      if (importedVariants.length === 0) {
        toast.error('Keine gültigen Varianten im Upload gefunden');
        return;
      }
      
      setFormData(prev => {
        const existing = normalizeVariants(prev.message_variants || []);
        const combined = normalizeVariants([...existing, ...importedVariants]);
        const messageValue = prev.message?.trim() || '';
        
        if (!messageValue) {
          return {
            ...prev,
            message: stripHtmlWrapper(combined[0] || ''),
            message_variants: combined.slice(1)
          };
        }
        
        return {
          ...prev,
          message_variants: combined.map(stripHtmlWrapper)
        };
      });
      
      toast.success(`${importedVariants.length} Nachricht(en) importiert`);
    } catch (error) {
      console.error('Failed to read uploaded templates:', error);
      toast.error('Upload fehlgeschlagen');
    } finally {
      event.target.value = '';
    }
  };

  const loadJobForReuse = React.useCallback(async (urlParams) => {
    try {
      // 🔧 Load from sessionStorage (für große HTMLs!)
      const sessionData = sessionStorage.getItem('jobCopyData');
      let basicData = {};
      
      if (sessionData) {
        // Aus sessionStorage laden (komplett mit HTML!)
        basicData = JSON.parse(sessionData);
        sessionStorage.removeItem('jobCopyData');  // Cleanup
        console.log('✅ Job-Daten aus sessionStorage geladen');
      } else {
        // Fallback: Aus URL params (für Rückwärtskompatibilität)
        basicData = {
          subject: urlParams.get('subject') || '',
          message: urlParams.get('message') || '',
          from_name: urlParams.get('from_name') || '',
          dynamic_from_email: urlParams.get('dynamic_from_email') || '',
          use_dynamic_from_email: urlParams.get('use_dynamic_from_email') === '1',
          reply_to_email: urlParams.get('reply_to_email') || '',
          emails_per_hour: parseInt(urlParams.get('emails_per_hour')) || 100,
          is_html: urlParams.get('is_html') === '1',
          ai_randomization_enabled: urlParams.get('ai_randomization_enabled') === '1',
          ai_randomization_frequency: urlParams.get('ai_randomization_frequency') || null,
          list_unsubscribe_enabled: urlParams.get('list_unsubscribe_enabled') === '1',
          list_unsubscribe_url: urlParams.get('list_unsubscribe_url') || '{Domain}/unsubscribe',
          list_unsubscribe_method: urlParams.get('list_unsubscribe_method') || 'oneclick',
          precedence_header: urlParams.get('precedence_header') === '1'
        };
      }
      
      const sourceJobId = basicData.source_job_id || urlParams.get('source_job_id');
      const recipientFilter = basicData.recipient_filter || urlParams.get('recipient_filter') || 'all';

      
      if (sourceJobId) {
        try {
          const response = await axios.get(`/api/jobs/${sourceJobId}`);
          const jobData = response.data.data;
          let slotConfig = jobData?.job?.slot_config || {};
          if (typeof slotConfig === 'string') {
            try {
              slotConfig = JSON.parse(slotConfig);
            } catch (slotParseError) {
              slotConfig = {};
            }
          }
          const slotEncoding = slotConfig?.text_encoding || slotConfig?.textEncoding;
          if (slotEncoding) {
            basicData.text_encoding = slotEncoding;
          }
          
          if (slotConfig?.burst_mode === true || jobData?.job?.send_method === 'burst') {
            basicData.send_method = 'burst';
            basicData.burst_mode = true;
          }
          if (typeof slotConfig?.burst_pause_seconds !== 'undefined') {
            const pauseValue = Number(slotConfig.burst_pause_seconds);
            basicData.burst_pause_seconds = Number.isFinite(pauseValue) ? pauseValue : 0;
          }
          
          let filteredRecipients = [];
          
          // Parse recipients safely (could be JSON array or string)
          let allRecipients = [];
          try {
            if (typeof jobData.job.recipients === 'string') {
              // Try to parse as JSON first
              try {
                allRecipients = JSON.parse(jobData.job.recipients);
              } catch (jsonError) {
                // If JSON parsing fails, treat as newline-separated string
                allRecipients = jobData.job.recipients.split('\n').map(r => r.trim()).filter(r => r);
              }
            } else if (Array.isArray(jobData.job.recipients)) {
              allRecipients = jobData.job.recipients;
            }
          } catch (error) {
            console.error('Error parsing recipients:', error);
            allRecipients = [];
          }

          if (recipientFilter === 'pending') {
            // Get recipients that haven't been sent to
            const sentRecipients = jobData.logs.filter(log => log.status === 'SENT').map(log => log.recipient);
            filteredRecipients = allRecipients.filter(recipient => !sentRecipients.includes(recipient));
          } else if (recipientFilter === 'failed') {
            // Get recipients that failed
            filteredRecipients = jobData.logs.filter(log => log.status === 'FAILED').map(log => log.recipient);
          } else if (recipientFilter === 'pending_failed') {
            // Get pending + failed recipients
            const sentRecipients = jobData.logs.filter(log => log.status === 'SENT').map(log => log.recipient);
            const pendingRecipients = allRecipients.filter(recipient => !sentRecipients.includes(recipient));
            const failedRecipients = jobData.logs.filter(log => log.status === 'FAILED').map(log => log.recipient);
            filteredRecipients = [...new Set([...pendingRecipients, ...failedRecipients])];
          } else {
            // 'all' - use all recipients
            filteredRecipients = allRecipients;
          }
          
          basicData.recipients = filteredRecipients.join('\n');
          console.log(`📧 Loaded ${filteredRecipients.length} ${recipientFilter} recipients`);
          
        } catch (error) {
          console.error('Failed to load filtered recipients:', error);
          toast.error('Fehler beim Laden der Empfänger');
        }
      }
      
      setFormData(prev => ({
        ...prev,
        ...basicData
      }));
      
      toast.success('Job-Daten geladen!');
      
    } catch (error) {
      console.error('Failed to load job for reuse:', error);
      toast.error('Fehler beim Laden der Job-Daten');
    }
  }, []);

  // Load job data from URL parameters (for job reuse)
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reuse') === 'true') {
      loadJobForReuse(urlParams);
    }
  }, [loadJobForReuse]);

  // Load SMTP servers
  const { data: smtpServers } = useQuery('smtpServers', async () => {
    const response = await axios.get('/api/smtp');
    return response.data.data;
  });

  // Load Webmail accounts
  const { data: webmailAccounts } = useQuery('webmailAccounts', async () => {
    const response = await axios.get('/api/webmailer');
    return response.data.data || [];
  });

  // Load templates
  const { data: templates } = useQuery('templates', async () => {
    const response = await axios.get('/api/templates');
    return response.data.data;
  });

  // Alte Proxy-useEffects entfernt - durch neues System ersetzt

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Parse recipients
      const recipientList = formData.recipients
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && line.includes('@'));

      if (recipientList.length === 0) {
        toast.error('Keine gültigen E-Mail-Adressen gefunden');
        setLoading(false);
        return;
      }

      const primaryMessage = formData.message?.trim() || '';
      const extraVariants = (formData.message_variants || [])
        .map((variant) => (variant || '').trim())
        .filter((variant) => variant.length > 0);
      const rotationTemplatesRaw = [primaryMessage, ...extraVariants].filter(Boolean);
      const rotationTemplates = Array.from(new Set(rotationTemplatesRaw));
      
      const effectiveMessage = primaryMessage || rotationTemplates[0] || '';
      if (!effectiveMessage) {
        toast.error('Bitte gib mindestens eine Nachricht an');
        setLoading(false);
        return;
      }
      
      // Create job
      const jobData = {
        ...formData,
        message: effectiveMessage,
        rotation_templates: rotationTemplates,
        recipients: recipientList,
        job_id: `mail_${Date.now()}_${Math.random().toString(36).substring(2)}`
      };
      
      if (jobData.send_method === 'burst') {
        jobData.burst_mode = true;
        jobData.use_pooled_streams = false;
        jobData.pooled_streams_count = 0;
        jobData.max_parallel_smtps = 0;
        jobData.burst_pause_seconds = Math.max(0, Number(jobData.burst_pause_seconds) || 0);
      } else {
        jobData.burst_mode = false;
      }

      // 🌍 Proxy-Settings: DEAKTIVIERT (Proxy pro SMTP aus DB, kein Job-Override)

      const response = await axios.post('/api/jobs', jobData);

      if (response.data.success) {
        const jobIdFromResponse = response.data.data.job_id;
        
        // 🔗 Wenn Admin Links generiert wurden, generiere und speichere sie
        if (generatedLinksData && generatedLinksData.link_counts) {
          try {
            console.log('🔗 Generating links for job:', jobIdFromResponse);
            const allLinks = { logo: [], cta: [], unsubscribe: [] };
            let storyWrapRedirects = null;
            
            const requestedCounts = generatedLinksData.link_counts || {};
            const linkConfig = generatedLinksData.link_config || {};
            const linkTypes = ['logo', 'cta', 'unsubscribe'];

            const getTypeConfig = (type) => {
              if (linkConfig[type]) {
                return linkConfig[type];
              }
              const fallbackProvider = generatedLinksData.provider || 'aws';
              const fallbackTarget = generatedLinksData.use_custom_domains ? 'custom' : 'slot';
              const fallbackSlot = generatedLinksData.slot || (generatedLinksData.domains ? { domains: generatedLinksData.domains } : null);
              const fallbackTargets = generatedLinksData.target_url ? [generatedLinksData.target_url] : [];
              return {
                provider: fallbackProvider,
                target: fallbackTarget,
                slot: fallbackSlot,
                custom_targets: fallbackTargets
              };
            };

            // Kombinierte Requests: Gruppiere Typen mit gleichem Provider → 1 Request pro Domain
            // aws+searchapp → Typ geht in BEIDE Gruppen (aws + searchapp)
            let requestItems = [];
            const providerGroups = {};
            for (const type of linkTypes) {
              const cfg = getTypeConfig(type) || {};
              const provider = cfg.provider || 'aws';
              if (provider === 'aws+searchapp') {
                if (!providerGroups['aws']) providerGroups['aws'] = [];
                providerGroups['aws'].push(type);
                if (!providerGroups['searchapp']) providerGroups['searchapp'] = [];
                providerGroups['searchapp'].push(type);
              } else if (provider === 'share.google+searchapp') {
                if (!providerGroups['share.google']) providerGroups['share.google'] = [];
                providerGroups['share.google'].push(type);
                if (!providerGroups['searchapp']) providerGroups['searchapp'] = [];
                providerGroups['searchapp'].push(type);
              } else {
                if (!providerGroups[provider]) providerGroups[provider] = [];
                providerGroups[provider].push(type);
              }
            }
            
            for (const [provider, types] of Object.entries(providerGroups)) {
              const totalCounts = { logo: 0, cta: 0, unsubscribe: 0 };
              let region = 'frankfurt';
              let linkStyle = 'SHORT';
              const allDomains = [];
              const allCustomTargets = [];
              
              for (const type of types) {
                totalCounts[type] = Number(requestedCounts[type]) || 0;
                const cfg = getTypeConfig(type) || {};
                region = cfg.region || region;
                linkStyle = cfg.link_style || linkStyle;
                
                if (cfg.target === 'custom') {
                  const targets = Array.isArray(cfg.custom_targets) ? cfg.custom_targets.filter(Boolean) : [];
                  targets.forEach(t => { if (!allCustomTargets.includes(t)) allCustomTargets.push(t); });
                } else {
                  const domains = cfg.slot?.domains || [];
                  domains.forEach(d => { if (d && !allDomains.includes(d)) allDomains.push(d); });
                }
              }
              
              const domainPool = allCustomTargets.length > 0 ? allCustomTargets : allDomains;
              const totalLinks = totalCounts.logo + totalCounts.cta + totalCounts.unsubscribe;
              if (domainPool.length === 0 || totalLinks === 0) continue;
              
              // Verteile Links gleichmaessig auf alle Domains (wie MultiCampaign pro Segment)
              const numDomains = domainPool.length;
              for (let di = 0; di < numDomains; di++) {
                const domain = domainPool[di];
                const isCustom = allCustomTargets.includes(domain);
                const counts = { logo: 0, cta: 0, unsubscribe: 0 };
                for (const type of types) {
                  const total = Number(requestedCounts[type]) || 0;
                  const base = Math.floor(total / numDomains);
                  const extra = di < (total % numDomains) ? 1 : 0;
                  counts[type] = base + extra;
                }
                if (counts.logo + counts.cta + counts.unsubscribe > 0) {
                  requestItems.push({
                    provider, counts,
                    slot_domain: isCustom ? undefined : domain,
                    target_url: isCustom ? domain : undefined,
                    region, link_style: linkStyle,
                  });
                }
              }
            }
            
            if (requestItems.length === 0) {
              console.warn('⚠️ Keine Link-Requests geplant (keine Domains oder Counts)');
              return;
            }
            
            const sendLinkRequest = async ({ provider, counts, slot_domain, target_url, region, link_style }, staggerMs = 0, staggerIndex = 0) => {
              try {
                let apiEndpoint = '';
                let requestPayload = {};
                
                if (provider === 'aws') {
                  apiEndpoint = '/api/aws-redirect/request';
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    counts
                  };
                } else if (provider === 'searchapp') {
                  apiEndpoint = '/api/searchapp-redirect/request';
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    smtp_email: generatedLinksData.smtp_email || null,
                    counts
                  };
                } else if (provider === 'searchapp-s3') {
                  apiEndpoint = '/api/searchapp-s3-redirect/request';
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    smtp_email: generatedLinksData.smtp_email || null,
                    counts
                  };
                } else if (provider === 'hybrid-aws-share.google') {
                  // Legacy-Provider-Key, neue Kette: AWS -> Search.app -> Ziel.
                  const searchTarget = (target_url || slot_domain || '');
                  const resolvedTarget = searchTarget.startsWith('http')
                    ? searchTarget
                    : `https://${searchTarget}`;
                  let searchLinks = { logo: [], cta: [], unsubscribe: [] };

                  if (target_url && counts.logo > 0) {
                    const logoResponse = await axios.post('/api/searchapp-redirect/request', {
                      campaign_id: jobIdFromResponse,
                      segment_id: 0,
                      target_url: resolvedTarget,
                      smtp_email: generatedLinksData.smtp_email || null,
                      link_style: link_style || 'SHORT',
                      counts: { logo: 0, cta: counts.logo, unsubscribe: 0 }
                    });
                    const restCounts = {
                      logo: 0,
                      cta: counts.cta,
                      unsubscribe: counts.unsubscribe
                    };
                    let restResponse = null;
                    if (restCounts.cta + restCounts.unsubscribe > 0) {
                      restResponse = await axios.post('/api/searchapp-redirect/request', {
                        campaign_id: jobIdFromResponse,
                        segment_id: 0,
                        target_url: resolvedTarget,
                        smtp_email: generatedLinksData.smtp_email || null,
                        link_style: link_style || 'SHORT',
                        counts: restCounts
                      });
                    }
                    searchLinks = {
                      logo: logoResponse.data?.data?.links?.cta || [],
                      cta: restResponse?.data?.data?.links?.cta || [],
                      unsubscribe: restResponse?.data?.data?.links?.unsubscribe || []
                    };
                  } else {
                    const searchResponse = await axios.post('/api/searchapp-redirect/request', {
                      campaign_id: jobIdFromResponse,
                      segment_id: 0,
                      target_url: resolvedTarget,
                      smtp_email: generatedLinksData.smtp_email || null,
                      link_style: link_style || 'SHORT',
                      counts
                    });
                    searchLinks = searchResponse.data?.data?.links || searchLinks;
                  }

                  const awsHybridLinks = { logo: [], cta: [], unsubscribe: [] };
                  const requestKey = Math.random().toString(36).slice(2, 10);
                  for (const type of ['logo', 'cta', 'unsubscribe']) {
                    const sourceLinks = searchLinks[type] || [];
                    for (let index = 0; index < sourceLinks.length; index++) {
                      const awsResponse = await axios.post('/api/aws-redirect/request', {
                        campaign_id: jobIdFromResponse,
                        segment_id: `aws_hybrid_${requestKey}_${type}_${index}`,
                        region,
                        target_url: sourceLinks[index],
                        counts: { logo: 0, cta: 0, unsubscribe: 0, [type]: 1 }
                      });
                      const wrapped = awsResponse.data?.data?.links?.[type]?.[0];
                      if (!wrapped) {
                        throw new Error(`AWS-Hybrid-Wrapping für ${type} fehlgeschlagen`);
                      }
                      awsHybridLinks[type].push(wrapped);
                    }
                  }

                  return {
                    success: true,
                    data: { success: true, data: { links: awsHybridLinks } }
                  };
                } else if (provider === 'story-wrap') {
                  apiEndpoint = '/api/searchapp-redirect/request';
                  const storyTarget = (target_url || slot_domain || '');
                  const resolvedTarget = storyTarget.startsWith('http')
                    ? storyTarget
                    : `https://${storyTarget.replace(/^https?:\/\//, '')}`;
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    provider: 'story-wrap',
                    counts,
                    ...(slot_domain ? { slot_domain } : { target_url: resolvedTarget }),
                  };
                } else if (provider === 'search-app-wrap') {
                  apiEndpoint = '/api/searchapp-redirect/request';
                  const wrapTarget = (target_url || slot_domain || '');
                  const resolvedTarget = wrapTarget.startsWith('http')
                    ? wrapTarget
                    : `https://${wrapTarget.replace(/^https?:\/\//, '')}`;
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    provider: 'search-app-wrap',
                    counts,
                    ...(slot_domain ? { slot_domain } : { target_url: resolvedTarget }),
                  };
                } else if (provider === 'share.google') {
                  apiEndpoint = '/api/searchapp-redirect/request';
                  const shareTarget = (target_url || slot_domain || '');
                  const resolvedTarget = shareTarget.startsWith('http') ? shareTarget : 'https://' + shareTarget;
                  const logoIsCustom = target_url && counts.logo > 0;
                  
                  if (logoIsCustom) {
                    // Separater Request für Logo (als CTA-Typ → exakte URL ohne /logo Suffix)
                    console.log(`🔗 Share.Google Logo-Custom: ${counts.logo} Logo-Links als CTA angefordert → ${resolvedTarget}`);
                    const logoResp = await axios.post(apiEndpoint, {
                      campaign_id: jobIdFromResponse,
                      segment_id: 0,
                      target_url: resolvedTarget,
                      smtp_email: generatedLinksData.smtp_email || null,
                      provider: 'share.google',
                      link_style: 'SHORT',
                      counts: { logo: 0, cta: counts.logo, unsubscribe: 0 }
                    });
                    
                    // Separater Request für restliche Typen (CTA/Unsub) falls vorhanden
                    const restCounts = { logo: 0, cta: counts.cta, unsubscribe: counts.unsubscribe };
                    let restResp = null;
                    if (restCounts.cta + restCounts.unsubscribe > 0) {
                      restResp = await axios.post(apiEndpoint, {
                        campaign_id: jobIdFromResponse,
                        segment_id: 0,
                        target_url: resolvedTarget,
                        smtp_email: generatedLinksData.smtp_email || null,
                        provider: 'share.google',
                        link_style: 'SHORT',
                        counts: restCounts
                      });
                    }
                    
                    // Kombiniere: Logo-Request CTA → logo, Rest-Request normal
                    const combinedLinks = {
                      logo: logoResp.data?.data?.links?.cta || [],
                      cta: restResp?.data?.data?.links?.cta || [],
                      unsubscribe: restResp?.data?.data?.links?.unsubscribe || []
                    };
                    return { success: true, data: { success: true, data: { links: combinedLinks } } };
                  }
                  
                  requestPayload = {
                    campaign_id: jobIdFromResponse,
                    segment_id: 0,
                    target_url: resolvedTarget,
                    smtp_email: generatedLinksData.smtp_email || null,
                    provider: 'share.google',
                    link_style: 'SHORT',
                    counts
                  };
                }
                
                if (provider !== 'share.google' && provider !== 'story-wrap' && provider !== 'search-app-wrap') {
                  if (region && (provider === 'aws' || provider === 'searchapp-s3')) {
                    requestPayload.region = region;
                  }
                  if (link_style && (provider === 'searchapp' || provider === 'searchapp-s3')) {
                    requestPayload.link_style = link_style;
                  }
                  if (slot_domain) requestPayload.slot_domain = slot_domain;
                  if (target_url) requestPayload.target_url = target_url;
                }
                
                if (staggerMs > 0) {
                  await new Promise(resolve => setTimeout(resolve, staggerIndex * staggerMs));
                }
                
                const linkResponse = await axios.post(apiEndpoint, requestPayload);
                return { success: true, data: linkResponse.data };
              } catch (domainError) {
                const identifier = slot_domain || target_url || 'custom';
                console.error(`❌ Failed to generate links for ${identifier}:`, domainError.message);
                return { success: false, identifier, error: domainError.message };
              }
            };
            
            const collectResults = (results) => {
              results.forEach(result => {
                if (result.success && result.data.success && result.data.data?.links) {
                  const links = result.data.data.links;
                  if (Array.isArray(links['story-wrap']) && links['story-wrap'].length > 0) {
                    if (!storyWrapRedirects) {
                      storyWrapRedirects = {
                        enabled: true,
                        provider: 'story-wrap',
                        links: { 'story-wrap': [], story_cta: [], cta: [], unsubscribe: [] },
                      };
                    }
                    storyWrapRedirects.links['story-wrap'].push(...links['story-wrap']);
                    storyWrapRedirects.links.story_cta.push(...(links.story_cta || []));
                    storyWrapRedirects.links.cta.push(...(links.cta || []));
                    storyWrapRedirects.links.unsubscribe.push(...(links.unsubscribe || []));
                  } else if (
                    (Array.isArray(links['search-app-wrap']) && links['search-app-wrap'].length > 0)
                    || (Array.isArray(links.search_app_wrap_cta) && links.search_app_wrap_cta.length > 0)
                  ) {
                    if (!storyWrapRedirects) {
                      storyWrapRedirects = {
                        enabled: true,
                        provider: 'search-app-wrap',
                        links: { 'story-wrap': [], story_cta: [], cta: [], unsubscribe: [] },
                      };
                    }
                    storyWrapRedirects.links['story-wrap'].push(...(links['search-app-wrap'] || []));
                    storyWrapRedirects.links.story_cta.push(...(links.search_app_wrap_cta || []));
                    storyWrapRedirects.links.cta.push(...(links.cta || []));
                    storyWrapRedirects.links.unsubscribe.push(...(links.unsubscribe || []));
                  } else {
                    allLinks.logo.push(...(links.logo || []));
                    allLinks.cta.push(...(links.cta || []));
                    allLinks.unsubscribe.push(...(links.unsubscribe || []));
                  }
                }
              });
            };
            
            const searchappItems = requestItems.filter(item => item.provider === 'searchapp');
            const otherItems = requestItems.filter(item => item.provider !== 'searchapp');
            
            if (searchappItems.length > 0) {
              console.log(`🔗 SearchApp: ${searchappItems.length} Requests parallel`);
              const searchappResults = await Promise.all(
                searchappItems.map(item => sendLinkRequest(item))
              );
              collectResults(searchappResults);
            }
            
            // 🚀 BATCHES: 10 Requests gleichzeitig, dann 2s Pause (nur non-searchapp)
            const BATCH_SIZE = 10;
            
            for (let batchStart = 0; batchStart < otherItems.length; batchStart += BATCH_SIZE) {
              const batch = otherItems.slice(batchStart, batchStart + BATCH_SIZE);
              console.log(`🔗 Batch ${Math.floor(batchStart / BATCH_SIZE) + 1}/${Math.ceil(otherItems.length / BATCH_SIZE)}: ${batch.length} Requests`);
              
              const batchPromises = batch.map((item, batchIndex) => sendLinkRequest(item, 0, batchIndex));
              
              // Warte auf alle Requests in diesem Batch
              const batchResults = await Promise.all(batchPromises);
              
              // Sammle Links aus diesem Batch
              collectResults(batchResults);
              
              // 2s Pause zwischen Batches (außer beim letzten)
              if (batchStart + BATCH_SIZE < otherItems.length) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
            
            // Speichere alle Links in der DB
            await axios.post('/api/admin/save-job-links', {
              job_id: jobIdFromResponse,
              provider: generatedLinksData.link_config ? 'mixed' : generatedLinksData.provider,
              slot_domain: generatedLinksData.target_url || null,
              links: allLinks,
              ...(storyWrapRedirects ? { story_wrap_redirects: storyWrapRedirects } : {}),
            });
            
            const storyWrapTotal = storyWrapRedirects
              ? (storyWrapRedirects.links['story-wrap']?.length || 0)
                + (storyWrapRedirects.links.story_cta?.length || 0)
                + (storyWrapRedirects.links.unsubscribe?.length || 0)
              : 0;
            console.log('✅ Admin links generated and saved:', allLinks, storyWrapRedirects || '');
            toast.success(`✅ ${allLinks.logo.length + allLinks.cta.length + allLinks.unsubscribe.length + storyWrapTotal} Links generiert!`);
          } catch (linkError) {
            console.error('❌ Failed to generate/save admin links:', linkError);
            toast.error('Fehler beim Generieren der Links: ' + (linkError.response?.data?.error || linkError.message));
          }
        }
        
        // 🤖 Wenn AI-Randomisierung: Zeige Progress
        console.log('🔍 AI-Check Frontend:', jobData.ai_randomization_enabled);
        if (jobData.ai_randomization_enabled) {
          console.log('🤖 Starting AI progress tracking...');
          setAiGenerating(true);
          toast.success('Job erstellt! AI-Randomisierung läuft...');
          
          // Poll für AI-Generation Progress
          const pollInterval = setInterval(async () => {
            try {
              const progressResponse = await axios.get(`/api/jobs/${jobIdFromResponse}/ai-progress`);
              const { ai_generation_status, ai_generation_progress, ai_generation_total } = progressResponse.data.data;
              
              setAiProgress({ current: ai_generation_progress || 0, total: ai_generation_total || 0 });
              
              if (ai_generation_status === 'completed') {
                clearInterval(pollInterval);
                setAiGenerating(false);
                toast.success(`✅ AI-Randomisierung abgeschlossen! ${ai_generation_total} Versionen generiert`);
                
                // Reset form NACH AI-Completion
                setFormData({
                  recipients: '',
                  subject: '',
                  message: '',
                  from_name: '',
                  dynamic_from_email: '',
                  use_dynamic_from_email: false,
                  reply_to_email: '',
                  emails_per_hour: 100,
                  is_html: true,
                  templates: [],
                  attachments: [],
                  selected_smtp_servers: [],
                  send_method: 'smtp',
                  selected_webmail_accounts: [],
                  webmail_parallel_threads: 2,  // Max 2 parallele Sessions pro Account
                  webmail_text_type: 'html',
                  burst_mode: false,
                  burst_pause_seconds: 0,
                  text_encoding: 'quoted-printable',
                  ai_randomization_enabled: false,
                  ai_randomization_frequency: 'every_100',
                  list_unsubscribe_enabled: false,
                  list_unsubscribe_url: '{Domain}/unsubscribe',
                  list_unsubscribe_method: 'oneclick',
                  precedence_header: false
                });
                
              } else if (ai_generation_status === 'failed') {
                clearInterval(pollInterval);
                setAiGenerating(false);
                toast.error('AI-Randomisierung fehlgeschlagen');
              }
            } catch (error) {
              console.error('Progress poll error:', error);
            }
          }, 2000); // Alle 2 Sekunden prüfen
          
          // Timeout nach 5 Minuten
          setTimeout(() => {
            clearInterval(pollInterval);
            setAiGenerating(false);
          }, 300000);
          
          // KEIN Form-Reset oder Redirect während AI läuft!
          return; // Früher Return, verhindert Reset unten
        } else {
        toast.success(`Job erstellt! ${recipientList.length} Empfänger`);
        }
        
        // Reset form (nur wenn KEIN AI)
        setFormData({
          recipients: '',
          subject: '',
          message: '',
          from_name: '',
          dynamic_from_email: '',
          use_dynamic_from_email: false,
          reply_to_email: '',
          emails_per_hour: 100,
          is_html: true,
          templates: [],
          attachments: [],
          selected_smtp_servers: [],
          send_method: 'smtp',
          selected_webmail_accounts: [],
          webmail_parallel_threads: 2,  // Max 2 parallele Sessions pro Account
          webmail_text_type: 'html',
          burst_mode: false,
          burst_pause_seconds: 0,
          text_encoding: 'quoted-printable',
          ai_randomization_enabled: false,
          ai_randomization_frequency: 'every_100',
          list_unsubscribe_enabled: false,
          list_unsubscribe_url: '{Domain}/unsubscribe',
          list_unsubscribe_method: 'oneclick',
          precedence_header: false
        });

        if (onJobCreated) {
          onJobCreated(response.data.data);
        }
      }
    } catch (error) {
      console.error('❌ Job creation error:', error);
      toast.error('Fehler beim Erstellen des Jobs');
    } finally {
      setLoading(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Datei ${file.name} ist zu groß (max. 10MB)`);
          continue;
        }

        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', file);

        // Upload file
        const response = await axios.post('/api/attachments/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          uploadedFiles.push({
            id: response.data.data.id,
            name: file.name,
            size: file.size,
            type: file.type,
            path: response.data.data.path
          });
        }
      }

      // Add to form data
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));

      toast.success(`${uploadedFiles.length} Datei(en) erfolgreich hochgeladen`);
      
    } catch (error) {
      toast.error('Fehler beim Hochladen der Dateien');
      console.error('Upload error:', error);
    } finally {
      setUploadingFiles(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
        <Mail className="h-6 w-6 mr-2 text-blue-600" />
        Neuen Mail-Job erstellen
      </h2>

      {/* 🤖 AI-Generation Progress Overlay */}
      {aiGenerating && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-secondary rounded-lg p-8 max-w-md w-full border dark:border-dark-copper-mid">
            <h3 className="text-xl font-semibold mb-4 text-center text-gray-900 dark:text-gray-100">
              🤖 AI-Content-Randomisierung läuft...
            </h3>
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-purple-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${aiProgress.total > 0 ? (aiProgress.current / aiProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-center mt-2 text-sm text-gray-600 dark:text-gray-200">
                {aiProgress.current} / {aiProgress.total} Versionen generiert
              </p>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Bitte warten... Wir generieren randomisierte E-Mail-Versionen für dich..
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* SMTP Server Selection - Ganz oben */}
        {/* Versandmethode Auswahl */}
        <div className="bg-indigo-50 dark:bg-dark-surface p-4 rounded-lg border border-indigo-200 dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Mail className="h-5 w-5 mr-2 text-indigo-600" />
            Versandmethode
          </h3>
          <div className="flex gap-4 mb-4">
            <button
              type="button"
              onClick={() => setSendMethod('smtp')}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                formData.send_method === 'smtp'
                  ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">SMTP</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Klassischer E-Mail-Versand</div>
            </button>
            <button
              type="button"
              onClick={() => setSendMethod('burst')}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                formData.send_method === 'burst'
                  ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">Burst (SMTP)</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Alle SMTPs parallel, ohne Rate-Limit</div>
            </button>
            <button
              type="button"
              onClick={() => setSendMethod('webmail')}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                formData.send_method === 'webmail'
                  ? 'border-indigo-500 bg-indigo-100 dark:bg-indigo-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300'
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">Webmail</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">T-Online OAuth (~3600/h pro 10 Acc)</div>
            </button>
          </div>
        </div>

        {/* SMTP-Server Auswahl - nur bei SMTP-Methode */}
        {(formData.send_method === 'smtp' || formData.send_method === 'burst') && smtpServers && smtpServers.length > 0 && (
          <div className="bg-purple-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Server className="h-5 w-5 mr-2 text-blue-600" />
              SMTP-Server-Auswahl
            </h3>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="smtp_selection"
                  value="all"
                  checked={formData.selected_smtp_servers.length === 0}
                  onChange={() => setFormData({...formData, selected_smtp_servers: []})}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Alle verfügbaren SMTP-Server verwenden (Automatische Rotation)</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="smtp_selection"
                  value="specific"
                  checked={formData.selected_smtp_servers.length > 0}
                  onChange={() => {
                    if (formData.selected_smtp_servers.length === 0) {
                      setFormData({...formData, selected_smtp_servers: [smtpServers[0]?.id]});
                    }
                  }}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Spezifische SMTP-Server auswählen:</span>
              </label>
              
              {formData.selected_smtp_servers.length > 0 && (
                <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {smtpServers.map((server) => (
                    <label key={server.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.selected_smtp_servers.includes(server.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              selected_smtp_servers: [...formData.selected_smtp_servers, server.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              selected_smtp_servers: formData.selected_smtp_servers.filter(id => id !== server.id)
                            });
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                          {server.host}:{server.port}
                        </div>
                        <div className="text-xs text-gray-500">
                          {server.from_email || server.username}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Webmail-Accounts Auswahl - nur bei Webmail-Methode */}
        {formData.send_method === 'webmail' && (
          <div className="bg-teal-50 dark:bg-dark-surface p-4 rounded-lg border border-teal-200 dark:border-dark-copper-dark">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <Mail className="h-5 w-5 mr-2 text-teal-600" />
              Webmail-Accounts
            </h3>
            
            {/* Parallel Threads Slider */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Parallele Threads: {formData.webmail_parallel_threads}
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={formData.webmail_parallel_threads}
                onChange={(e) => setFormData({...formData, webmail_parallel_threads: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1</span>
                <span>25</span>
                <span>50</span>
              </div>
            </div>

            {/* Text Type Auswahl */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Content-Typ
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="webmail_text_type"
                    value="html"
                    checked={formData.webmail_text_type === 'html'}
                    onChange={() => setFormData({...formData, webmail_text_type: 'html'})}
                    className="text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">HTML</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="webmail_text_type"
                    value="plain"
                    checked={formData.webmail_text_type === 'plain'}
                    onChange={() => setFormData({...formData, webmail_text_type: 'plain'})}
                    className="text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Plain Text</span>
                </label>
              </div>
            </div>

            {/* Account Auswahl */}
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="webmail_selection"
                  value="all"
                  checked={formData.selected_webmail_accounts.length === 0}
                  onChange={() => setFormData({...formData, selected_webmail_accounts: []})}
                  className="text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Alle aktiven Webmail-Accounts verwenden</span>
              </label>
              
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="webmail_selection"
                  value="specific"
                  checked={formData.selected_webmail_accounts.length > 0}
                  onChange={() => {
                    if (formData.selected_webmail_accounts.length === 0 && webmailAccounts?.length > 0) {
                      setFormData({...formData, selected_webmail_accounts: [webmailAccounts[0]?.id]});
                    }
                  }}
                  className="text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Spezifische Accounts auswählen:</span>
              </label>
              
              {formData.selected_webmail_accounts.length > 0 && webmailAccounts && (
                <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {webmailAccounts.filter(acc => acc.is_active).map((account) => (
                    <label key={account.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.selected_webmail_accounts.includes(account.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              selected_webmail_accounts: [...formData.selected_webmail_accounts, account.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              selected_webmail_accounts: formData.selected_webmail_accounts.filter(id => id !== account.id)
                            });
                          }
                        }}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <div className="flex-1 truncate">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
                          {account.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {account.label || 'T-Online'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              
              {(!webmailAccounts || webmailAccounts.length === 0) && (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Keine Webmail-Accounts vorhanden. <a href="/webmailer" className="text-teal-600 hover:underline">Jetzt hinzufügen</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Burst-Settings */}
        {formData.send_method === 'burst' && (
          <div className="bg-amber-50 dark:bg-dark-surface p-4 rounded-lg border border-amber-200 dark:border-dark-copper-dark">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <Server className="h-5 w-5 mr-2 text-amber-600" />
              Burst-Modus Einstellungen
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 mb-4">
              Kein Rate-Limit, keine Connection-Prewarm. Pro SMTP wird immer nur 1 E-Mail gleichzeitig gesendet.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Pause zwischen Provider-Bursts (Sekunden)
                </label>
                <input
                  type="number"
                  min="0"
                  max="3600"
                  value={formData.burst_pause_seconds}
                  onChange={(e) => {
                    const value = Math.max(0, parseInt(e.target.value, 10) || 0);
                    setFormData({ ...formData, burst_pause_seconds: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  0 = keine Pause zwischen den Bursts
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Provider-Reihenfolge
                </label>
                <p className="text-xs text-gray-500">
                  Automatisch nach Empfänger‑Anzahl pro Provider sortiert (größter zuerst).
                  Unbekannte Domains werden als <b>Rest</b> zusammengefasst.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Proxy (Info) - nur bei SMTP */}
        {(formData.send_method === 'smtp' || formData.send_method === 'burst') && (
          <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border border-green-200 dark:border-dark-copper-dark">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <Server className="h-5 w-5 mr-2 text-green-600" />
              Proxy
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Proxy wird automatisch <b>pro SMTP</b> aus der DB genutzt (711 Session). Keine Job-Proxy-Auswahl mehr.
            </p>
          </div>
        )}

        {/* Absender-Einstellungen */}
        <div className="bg-blue-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Send className="h-5 w-5 mr-2 text-blue-600" />
            Absender-Einstellungen
          </h3>
          
          {/* Absender (Name + E-Mail) */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Absender-Name * (Spintax + Variable Codes)
                </label>
                <label className="flex items-center text-xs text-gray-500 cursor-pointer" title="Fügt unsichtbare Zero-Width Zeichen ein für Anti-Spam">
                  <input
                    type="checkbox"
                    checked={formData.zerowidth_fromname || false}
                    onChange={(e) => setFormData({...formData, zerowidth_fromname: e.target.checked})}
                    className="mr-1 h-3 w-3"
                  />
                  🔮 ZW
                </label>
              </div>
              <input
                type="text"
                value={formData.from_name}
                onChange={(e) => setFormData({...formData, from_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max($)3Z($) | {Firma A|Firma B}"
                required
              />
                <p className="text-xs text-gray-500 mt-1">
                  ($)5Z($) • ($)3A($) • {'{Name1|Name2}'}
                </p>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="use_dynamic_from_email"
                    checked={formData.use_dynamic_from_email || false}
                    onChange={(e) => setFormData({...formData, use_dynamic_from_email: e.target.checked})}
                    className="mr-2"
                  />
                  <label htmlFor="use_dynamic_from_email" className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    📧 Dynamische Absender-E-Mail
                  </label>
                </div>
                
                {formData.use_dynamic_from_email && (
                  <input
                    type="text"
                    value={formData.dynamic_from_email || ''}
                    onChange={(e) => setFormData({...formData, dynamic_from_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="kontakt.($)7Z($)"
                  />
                )}
              </div>
            </div>

            {/* Erweitert (Reply-To + Rate-Limit) - Rate-Limit nur bei SMTP */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Erweiterte Einstellungen</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Antwort-E-Mail (Reply-To)
              </label>
              <input
                type="email"
                value={formData.reply_to_email}
                onChange={(e) => setFormData({...formData, reply_to_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional"
              />
            </div>

            {/* Rate-Limit nur bei SMTP */}
            {formData.send_method === 'smtp' && (
              <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                E-Mails pro Stunde
              </label>
              <input
                type="number"
                value={formData.emails_per_hour}
                onChange={(e) => setFormData({...formData, emails_per_hour: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                    max="100000"
              />
                </div>
                
                {/* Pooled Streams Option */}
                <div>
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id="use_pooled_streams"
                      checked={formData.use_pooled_streams}
                      onChange={(e) => setFormData({...formData, use_pooled_streams: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="use_pooled_streams" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                      ⚡ Pooled Streams (Paralleler Versand mit Connection-Pooling)
                    </label>
                  </div>
                  
                  {formData.use_pooled_streams && (
                    <div className="ml-6 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Anzahl Streams
                        </label>
                        <input
                          type="number"
                          value={formData.pooled_streams_count}
                          onChange={(e) => setFormData({...formData, pooled_streams_count: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          min="1"
                          max="10"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Empfohlen: 2-4 Streams für optimale Performance
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Max. parallele SMTPs
                        </label>
                        <input
                          type="number"
                          value={formData.max_parallel_smtps}
                          onChange={(e) => setFormData({...formData, max_parallel_smtps: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          min="1"
                          max="50"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Wie viele SMTP-Server gleichzeitig senden (Standard: 10)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {!formData.use_pooled_streams && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Sequenzielle Streams (ohne Pooling)
                    </label>
                    <input
                      type="number"
                      value={formData.sequential_streams}
                      onChange={(e) => {
                        const nextValue = Math.max(1, parseInt(e.target.value, 10) || 1);
                        setFormData({ ...formData, sequential_streams: nextValue });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="1"
                      max="50"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Parallele Worker im sequenziellen Versand (z.B. 10)
                    </p>
                  </div>
                )}
              </>
            )}
              </div>
            </div>
          </div>
        </div>

        {/* Features - Header-Optionen nur bei SMTP */}
        <div className="space-y-4">
          {/* AI-Content-Randomisierung */}
            <div>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="ai_randomization_enabled"
                  checked={formData.ai_randomization_enabled}
                  onChange={(e) => setFormData({...formData, ai_randomization_enabled: e.target.checked})}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="ai_randomization_enabled" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                  🤖 AI-Content-Randomisierung (Claude Sonnet 4.5)
                </label>
              </div>
              
              {formData.ai_randomization_enabled && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Randomisierung-Häufigkeit
                    </label>
                    <select
                      value={formData.ai_randomization_frequency}
                      onChange={(e) => setFormData({...formData, ai_randomization_frequency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {(() => {
                        const recipientCount = formData.recipients.split('\n').filter(r => r.includes('@')).length;
                        const maxVersions = Math.min(100, recipientCount);
                        
                        return (
                          <>
                            <option value="every_1" disabled={maxVersions > 100}>
                              🤖 Maximale Variation (~{Math.min(100, recipientCount)} Versionen)
                              {maxVersions > 100 && ' - Nicht verfügbar (zu viele Empfänger)'}
                            </option>
                            <option value="every_10">
                              🤖 Alle 500 Mails (bis zu {Math.min(100, Math.ceil(recipientCount / 500))} Versionen)
                            </option>
                            <option value="every_100">
                              🤖 Alle 500 Mails (bis zu {Math.min(100, Math.ceil(recipientCount / 500))} Versionen)
                            </option>
                            <option value="every_1000">
                              🤖 Alle 1000 Mails (bis zu {Math.min(100, Math.ceil(recipientCount / 1000))} Versionen)
                            </option>
                          </>
                        );
                      })()}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      ⚡ Links, Platzhalter und Spintax bleiben - nur HTML-Struktur wird randomisiert
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded p-3">
                    <p className="text-xs text-purple-800">
                      <strong>💡 Wichtig:</strong> AI-Pre-Generation läuft bei Job-Erstellung (max 100 Versionen).<br/>
                      <strong>⏱️ Dauer:</strong> ~2 Minuten für 100 Versionen (Claude Rate-Limit: 50/Min)<br/>
                      <strong>📊 Batch-System:</strong> Versionen werden automatisch auf Empfänger verteilt
                    </p>
                  </div>
                </div>
              )}
            </div>

          {/* List-Unsubscribe Header - nur bei SMTP */}
          {formData.send_method === 'smtp' && (
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Encoding (SMTP)
              </label>
              <select
                value={formData.text_encoding}
                onChange={(e) => setFormData({...formData, text_encoding: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="quoted-printable">quoted-printable (Standard)</option>
                <option value="base64">base64</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Gilt nur für SMTP.
              </p>
            </div>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="list_unsubscribe_enabled"
                checked={formData.list_unsubscribe_enabled}
                onChange={(e) => setFormData({...formData, list_unsubscribe_enabled: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="list_unsubscribe_enabled" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                List-Unsubscribe Header (RFC 8058)
              </label>
            </div>
            
            {formData.list_unsubscribe_enabled && (
              <div className="space-y-3 ml-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Methode
                  </label>
                  <select
                    value={formData.list_unsubscribe_method}
                    onChange={(e) => setFormData({...formData, list_unsubscribe_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="oneclick">One-Click (HTTP + Post Header)</option>
                    <option value="mailto">Mailto (E-Mail-basiert)</option>
                    <option value="both">Both (HTTP + Mailto)</option>
                  </select>
                </div>
                
                {formData.list_unsubscribe_method !== 'mailto' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Unsubscribe-URL
                    </label>
                    <input
                      type="text"
                      value={formData.list_unsubscribe_url}
                      onChange={(e) => setFormData({...formData, list_unsubscribe_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="{Domain}/unsubscribe oder {Domain}/unsub($)3($)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      📌 Platzhalter: &#123;Domain&#125;, &#123;MailDomain&#125;, ($)3($)
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Precedence Header - nur bei SMTP */}
          {formData.send_method === 'smtp' && (
          <div>
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="precedence_header"
                checked={formData.precedence_header}
                onChange={(e) => setFormData({...formData, precedence_header: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="precedence_header" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                Precedence: first-class Header
              </label>
            </div>
            
            {formData.precedence_header && (
              <div className="ml-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Info:</strong> Markiert E-Mails als persönliche Nachricht (nicht als Bulk/Marketing).<br/>
                  <strong>Effekt:</strong> Kann die Zustellrate bei manchen Providern verbessern.<br/>
                  <strong>Hinweis:</strong> Nur für persönliche/transaktionale E-Mails verwenden!
                </p>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Admin-Proxy-UI entfernt - jetzt oben bei allen Usern */}

        {/* Subject */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Betreff (Spintax unterstützt: {'{Option 1|Option 2}'})
            </label>
            <label className="flex items-center text-xs text-gray-500 cursor-pointer" title="Fügt unsichtbare Zero-Width Zeichen ein für Anti-Spam">
              <input
                type="checkbox"
                checked={formData.zerowidth_subject || false}
                onChange={(e) => setFormData({...formData, zerowidth_subject: e.target.checked})}
                className="mr-1 h-3 w-3"
              />
              🔮 ZW
            </label>
          </div>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) => setFormData({...formData, subject: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ihr Betreff hier..."
            required
          />
        </div>

        {/* Message */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              📄 Nachricht (HTML unterstützt)
            </label>
            <label className="flex items-center text-xs text-gray-500 cursor-pointer" title="Fügt unsichtbare Zero-Width Zeichen im Content ein für Anti-Spam">
              <input
                type="checkbox"
                checked={formData.zerowidth_content !== false}
                onChange={(e) => setFormData({...formData, zerowidth_content: e.target.checked})}
                className="mr-1 h-3 w-3"
              />
              🔮 ZW Content
            </label>
          </div>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
            onBlur={(e) => setFormData({...formData, message: stripHtmlWrapper(e.target.value)})}
            rows={12}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="<html><body>Ihre HTML-Nachricht hier...</body></html>"
            required
          />
          <div className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="strip_html_wrapper"
              checked={formData.strip_html_wrapper}
              onChange={(e) => setFormData({...formData, strip_html_wrapper: e.target.checked})}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="strip_html_wrapper" className="text-xs text-gray-600">
              HTML/Body-Tags entfernen
            </label>
          </div>
          <div className="mt-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                🔁 Zusätzliche Nachrichten-Varianten (Round-Robin)
              </div>
              <button
                type="button"
                onClick={handleAddMessageVariant}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                + Variante
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Basis-Nachricht + Varianten werden gleichmäßig verteilt. HTML-Upload unterstützt (optional mit <code>&lt;!--LETTER--&gt;</code> Split).
            </p>
            <input
              type="file"
              accept=".html,.htm,.txt"
              multiple
              onChange={handleMessageVariantsUpload}
              className="block w-full text-xs text-gray-600"
            />
            {(formData.message_variants || []).length > 0 && (
              <div className="space-y-3">
                {formData.message_variants.map((variant, index) => (
                  <div key={`variant_${index}`} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600">
                        Variante {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveMessageVariant(index)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Entfernen
                      </button>
                    </div>
                    <textarea
                      value={variant}
                      onChange={(e) => handleMessageVariantChange(index, e.target.value)}
                      onBlur={(e) => handleMessageVariantChange(index, stripHtmlWrapper(e.target.value))}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                      placeholder="Alternative Nachricht..."
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recipients */}
        <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            Empfänger (eine E-Mail pro Zeile)
          </label>
          <textarea
            value={formData.recipients}
            onChange={(e) => setFormData({...formData, recipients: e.target.value})}
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="beispiel@domain.de&#10;test@example.com&#10;..."
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.recipients.split('\n').filter(line => line.trim() && line.includes('@')).length} gültige E-Mails erkannt
          </p>
        </div>


        {/* Templates Selection - Collapsible */}
        {templates && templates.length > 0 && (
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowTemplates(!showTemplates)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">
                  Templates verwenden ({formData.templates.length} ausgewählt)
                </span>
              </div>
              <div className={`transform transition-transform ${showTemplates ? 'rotate-180' : ''}`}>
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            
            {showTemplates && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {templates.map((template) => (
                    <label key={template.id} className="flex items-center space-x-2 p-3 border rounded-md hover:bg-white bg-white transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.templates.includes(template.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, templates: [...formData.templates, template.id]});
                          } else {
                            setFormData({...formData, templates: formData.templates.filter(id => id !== template.id)});
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-200">{template.name}</div>
                        <div className="text-xs text-gray-500 truncate">{template.description || 'Keine Beschreibung'}</div>
                        <div className="text-xs text-blue-600 mt-1">
                          {Math.round((template.content_length || 0) / 1024)} KB
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  💡 Tipp: Templates werden in der Reihenfolge der Auswahl angewendet
                </div>
              </div>
            )}
          </div>
        )}


        {/* Attachments Section */}
        <div className="bg-green-50 dark:bg-dark-surface p-4 rounded-lg border dark:border-dark-copper-dark">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center">
              <Paperclip className="h-5 w-5 mr-2 text-blue-600" />
              Anhänge (optional)
            </h3>
            <label className="flex items-center text-xs text-gray-600 dark:text-gray-300 cursor-pointer" title="Randomisiert PDF/Bild-Metadaten und Hashes pro E-Mail (verhindert Spam-Erkennung)">
              <input
                type="checkbox"
                checked={formData.randomize_attachments !== false}
                onChange={(e) => setFormData({...formData, randomize_attachments: e.target.checked})}
                className="mr-1 h-3 w-3"
              />
              🔀 Randomize Files
            </label>
          </div>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Dateien hochladen
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt"
                  disabled={uploadingFiles}
                />
                <label htmlFor="file-upload" className={`cursor-pointer ${uploadingFiles ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600">
                    {uploadingFiles ? 'Upload läuft...' : 'Klicken Sie hier oder ziehen Sie Dateien hierher'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, Bilder, Dokumente (max. 10MB pro Datei)
                  </p>
                </label>
              </div>
            </div>

            {/* Uploaded Files List */}
            {formData.attachments.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Hochgeladene Dateien ({formData.attachments.length})
                </label>
                <div className="space-y-2">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-md">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-200">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({Math.round(file.size / 1024)}KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 🔗 Job Link Generation Panel - vor Submit */}
        <AdminLinkPanel
          isAdmin={isAdmin}
          onLinksConfigured={(config) => {
            setGeneratedLinksData(config);
            toast.success('Link-Konfiguration gespeichert! Links werden beim Job-Start generiert.');
          }}
        />

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            onClick={() => {
              setFormData({
                recipients: '',
                subject: '',
                message: '',
                from_name: '',
                dynamic_from_email: '',
                use_dynamic_from_email: false,
                reply_to_email: '',
                emails_per_hour: 100,
                is_html: true,
                templates: [],
                attachments: [],
                send_method: 'smtp',
                selected_webmail_accounts: [],
                webmail_parallel_threads: 2,
                webmail_text_type: 'html',
                burst_mode: false,
                burst_pause_seconds: 0,
                use_pooled_streams: false,
                pooled_streams_count: 2,
                max_parallel_smtps: 10,
                sequential_streams: 10,
                message_variants: [],
                strip_html_wrapper: true,
                text_encoding: 'quoted-printable',
                ai_randomization_enabled: false,
                ai_randomization_frequency: 'every_100',
                zerowidth_subject: false,
                zerowidth_fromname: false,
                zerowidth_content: true,
                randomize_attachments: true,
                list_unsubscribe_enabled: false,
                list_unsubscribe_url: '{Domain}/unsubscribe',
                list_unsubscribe_method: 'oneclick',
                precedence_header: false
              });
            }}
          >
            Zurücksetzen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Erstelle Job...' : 'Job erstellen'}
          </button>
        </div>
      </form>

      {/* HTML Preview Section */}
      {formData.is_html && formData.message && (
        <div className="mt-6">
          <HtmlPreview
            htmlContent={formData.message}
            subject={formData.subject}
            isVisible={showPreview}
            onToggle={() => setShowPreview(!showPreview)}
          />
        </div>
      )}
    </div>
  );
};

export default CreateJob;
